import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Phone, MessageCircle, Plus, Search, Trash2, Pencil, FileText, Wallet } from "lucide-react";
import { toast } from "sonner";

function CustomerForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(
    initial || {
      name: "", shop_name: "", primary_phone: "", alternate_phones: [], address: "", notes: "",
      opening_outstanding_amount: 0, opening_metal_type: "", opening_pending_weight: 0,
      opening_balance_date: "", opening_remarks: "", opening_outstanding_type: "",
    }
  );
  const [altInput, setAltInput] = useState("");

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      data-testid="customer-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Shop Name</Label>
          <Input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Primary Phone *</Label>
          <Input required value={form.primary_phone} onChange={(e) => setForm({ ...form, primary_phone: e.target.value })} data-testid="customer-phone-input" />
        </div>
        <div className="space-y-2">
          <Label>Alternate Phone</Label>
          <div className="flex gap-2">
            <Input
              placeholder="Add and press +"
              value={altInput}
              onChange={(e) => setAltInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && altInput.trim()) {
                  e.preventDefault();
                  setForm({ ...form, alternate_phones: [...form.alternate_phones, altInput.trim()] });
                  setAltInput("");
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (altInput.trim()) {
                  setForm({ ...form, alternate_phones: [...form.alternate_phones, altInput.trim()] });
                  setAltInput("");
                }
              }}
            >
              +
            </Button>
          </div>
          {form.alternate_phones.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {form.alternate_phones.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-zinc-100 text-xs px-2 py-1 rounded">
                  {p}
                  <button
                    type="button"
                    className="text-zinc-500 hover:text-red-600"
                    onClick={() => setForm({ ...form, alternate_phones: form.alternate_phones.filter((_, j) => j !== i) })}
                  >×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Address</Label>
        <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>

      {/* Opening Balance section */}
      <div className="border-t border-border pt-4 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Opening Balance (Carry-Forward)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Outstanding Amount (₹)</Label>
            <Input type="number" step="0.01" value={form.opening_outstanding_amount || 0}
              onChange={(e) => setForm({ ...form, opening_outstanding_amount: parseFloat(e.target.value) || 0 })}
              data-testid="opening-amount-input" />
          </div>
          <div className="space-y-2">
            <Label>Outstanding Type</Label>
            <select className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
              value={form.opening_outstanding_type || ""}
              onChange={(e) => setForm({ ...form, opening_outstanding_type: e.target.value })}
              data-testid="opening-type-select">
              <option value="">— Select —</option>
              <option value="receivable">Customer has to PAY us</option>
              <option value="payable">We have to PAY customer</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Metal Type</Label>
            <select className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
              value={form.opening_metal_type || ""}
              onChange={(e) => setForm({ ...form, opening_metal_type: e.target.value })}>
              <option value="">— None —</option>
              <option value="Gold">Gold</option>
              <option value="Silver">Silver</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label>Pending Weight (g)</Label>
            <Input type="number" step="0.001" value={form.opening_pending_weight || 0}
              onChange={(e) => setForm({ ...form, opening_pending_weight: parseFloat(e.target.value) || 0 })} />
          </div>
          <div className="space-y-2">
            <Label>Opening Balance Date</Label>
            <Input type="date" value={(form.opening_balance_date || "").slice(0, 10)}
              onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Remarks</Label>
            <Input value={form.opening_remarks || ""} onChange={(e) => setForm({ ...form, opening_remarks: e.target.value })} />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="customer-form-submit">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function CustomersList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const navigate = useNavigate();

  const load = async (search = "") => {
    const { data } = await api.get("/customers", { params: search ? { q: search } : {} });
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (form) => {
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setItems([data, ...items]);
    } catch (e) {
      toast.error("Could not add customer");
    }
  };

  const onUpdate = async (form) => {
    try {
      const { data } = await api.put(`/customers/${editTarget.id}`, form);
      toast.success("Customer updated");
      setEditTarget(null);
      setItems(items.map((c) => (c.id === data.id ? data : c)));
    } catch {
      toast.error("Could not update");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await api.delete(`/customers/${id}`);
      setItems(items.filter((c) => c.id !== id));
      toast.success("Customer deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Directory</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Customers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-customer-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Customer</DialogTitle>
            </DialogHeader>
            <CustomerForm onSubmit={onCreate} submitLabel="Add Customer" />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
        <Input
          placeholder="Search by name, shop, phone…"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          className="pl-9"
          data-testid="customer-search-input"
        />
      </div>

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No customers yet.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="customer-list">
            {items.map((c) => (
              <div key={c.id} className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] items-center gap-3 p-4 hover:bg-zinc-50">
                <Link to={`/customers/${c.id}`} className="min-w-0">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{c.shop_name || "—"} · {c.address || "no address"}</div>
                </Link>
                <div className="text-sm num">{c.primary_phone}</div>
                <div className="flex items-center gap-1 justify-end flex-wrap">
                  <a href={`tel:${c.primary_phone}`}>
                    <Button size="icon" variant="outline" className="h-8 w-8" title="Call"><Phone className="w-3.5 h-3.5" /></Button>
                  </a>
                  <a href={`https://wa.me/${c.primary_phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                    <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600 hover:text-emerald-700" title="WhatsApp"><MessageCircle className="w-3.5 h-3.5" /></Button>
                  </a>
                  <Link to={`/payments?customer=${c.id}`}>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Payment History">
                      <Wallet className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Statement PDF"
                    onClick={async () => {
                      try {
                        const r = await api.get(`/customers/${c.id}/statement-pdf`, { responseType: "blob" });
                        const blob = new Blob([r.data], { type: "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `statement-${c.id}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => {
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }, 200);
                      } catch { toast.error("Could not open statement PDF"); }
                    }}
                    data-testid={`statement-pdf-${c.id}`}>
                    <FileText className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => setEditTarget(c)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" title="Delete" onClick={() => onDelete(c.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {editTarget && <CustomerForm initial={editTarget} onSubmit={onUpdate} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
